import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { finalize } from 'rxjs';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { FinanceApiService } from '../finance/finance-api.service';
import { SalesApiService, SalesChannelRecord, SalesOrderListItem } from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-orders',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule, SelectModule, AinDatePipe],
  template: `
    <ain-page [title]="pageTitle()" [description]="pageDescription()">
      @if (initialLoading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('sales.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="refresh()"></button>
          </div>

          <div class="filter-toggle">
            <button pButton type="button" [label]="showFilters() ? t('sales.filter.hide') : t('sales.filter.show')" severity="secondary" (click)="toggleFilters()"></button>
          </div>

          @if (showFilters()) {
            <div class="filters">
              <label>
                <span>{{ t('sales.orders.fields.search') }}</span>
                <input pInputText type="text" [(ngModel)]="filters.search" />
              </label>
              <label>
                <span>{{ t('columns.status') }}</span>
                <p-select [options]="statusOptions" optionValue="value" optionLabel="label" [(ngModel)]="filters.status" [showClear]="true" appendTo="body" />
              </label>
              <label>
                <span>{{ t('sales.orders.fields.channel') }}</span>
                <p-select [options]="channels()" optionLabel="name" optionValue="id" [(ngModel)]="filters.channelId" [showClear]="true" appendTo="body" />
              </label>
              <div class="filter-actions">
                <button pButton type="button" [label]="t('finance.banks.search')" (click)="applyFilters()"></button>
              </div>
            </div>
          }

          <p-table
            [value]="rows()"
            dataKey="id"
            [paginator]="true"
            [rows]="pageSize"
            [first]="tableFirst()"
            [totalRecords]="totalCount()"
            [lazy]="true"
            [loading]="tableLoading()"
            (onLazyLoad)="onLazyLoad($event)"
            [selection]="selectedRows()"
            (selectionChange)="onSelectionChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('sales.orders.fields.orderNumber') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th>{{ t('sales.orders.fields.channel') }}</th>
                <th>{{ t('sales.orders.fields.customer') }}</th>
                <th>{{ t('sales.orders.fields.currency') }}</th>
                <th>{{ t('sales.orders.fields.createdAt') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.orderNumber }}</button></td>
                <td>{{ row.status }}</td>
                <td>{{ channelLabel(row.salesChannelId) }}</td>
                <td>{{ customerLabel(row.customerAccountId) }}</td>
                <td>{{ row.currencyCode }}</td>
                <td>{{ row.createdAtUtc | ainDate:'datetime' }}</td>
              </tr>
            </ng-template>
          </p-table>

          @if (error()) {
            <p class="error">{{ error() }}</p>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar, .filter-actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .filter-toggle { display: flex; }
    .filters { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); align-items: end; }
    .filters label { display: grid; gap: 6px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { margin: 0; color: #b91c1c; }
  `]
})
export class SalesOrdersComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly financeApi = inject(FinanceApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly localization = inject(LocalizationService);

  readonly initialLoading = signal(true);
  readonly tableLoading = signal(false);
  readonly error = signal('');
  readonly showFilters = signal(true);
  readonly rows = signal<SalesOrderListItem[]>([]);
  readonly selectedRows = signal<SalesOrderListItem[]>([]);
  readonly channels = signal<SalesChannelRecord[]>([]);
  readonly customers = signal<Map<string, string>>(new Map());
  readonly totalCount = signal(0);
  readonly tableFirst = signal(0);
  readonly pageSize = 15;
  pageNumber = 1;

  readonly statusOptions = [
    { value: 'Draft', label: 'Draft' },
    { value: 'Submitted', label: 'Submitted' },
    { value: 'Cancelled', label: 'Cancelled' }
  ];

  readonly filters = { search: '', status: null as string | null, channelId: null as string | null };

  private loadInFlight = false;
  private lastLazyFirst = -1;
  private readonly proformaContext = this.route.snapshot.data['documentContext'] === 'proforma';

  ngOnInit(): void {
    this.restoreFiltersFromQuery();
    let channelsDone = false;
    let customersDone = false;

    const tryFinishInitialShell = () => {
      if (channelsDone && customersDone) {
        this.initialLoading.set(false);
      }
    };

    this.api.listChannels().subscribe({
      next: channels => this.channels.set(channels),
      error: () => this.channels.set([]),
      complete: () => {
        channelsDone = true;
        tryFinishInitialShell();
      }
    });
    this.financeApi.listPlatformCustomers({ take: 500 }).subscribe({
      next: response => {
        const map = new Map<string, string>();
        for (const item of response.items ?? []) {
          map.set(item.id, `${item.displayName} #${item.customerCode}`);
        }
        this.customers.set(map);
      },
      error: () => this.customers.set(new Map()),
      complete: () => {
        customersDone = true;
        tryFinishInitialShell();
      }
    });
  }

  toggleFilters(): void {
    this.showFilters.set(!this.showFilters());
    this.syncQueryParams();
  }

  applyFilters(): void {
    this.pageNumber = 1;
    this.tableFirst.set(0);
    this.lastLazyFirst = -1;
    this.syncQueryParams();
    this.fetchOrders();
  }

  refresh(): void {
    this.fetchOrders();
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    if (this.loadInFlight) {
      return;
    }

    const first = event.first ?? 0;
    const rows = event.rows ?? this.pageSize;
    if (first === this.lastLazyFirst && this.rows().length > 0) {
      return;
    }

    this.lastLazyFirst = first;
    this.tableFirst.set(first);
    this.pageNumber = Math.floor(first / rows) + 1;
    this.syncQueryParams();
    this.fetchOrders();
  }

  private fetchOrders(): void {
    if (this.loadInFlight) {
      return;
    }

    this.loadInFlight = true;
    this.tableLoading.set(true);
    this.error.set('');
    this.api.listOrders({
      page: this.pageNumber,
      pageSize: this.pageSize,
      search: this.filters.search || undefined,
      status: this.filters.status || undefined,
      channelId: this.filters.channelId || undefined,
      documentKind: this.proformaContext ? 'Proforma' : undefined
    }).pipe(
      finalize(() => {
        this.loadInFlight = false;
        this.tableLoading.set(false);
      })
    ).subscribe({
      next: result => {
        this.rows.set(result.items);
        this.totalCount.set(result.totalCount);
      },
      error: () => {
        this.rows.set([]);
        this.totalCount.set(0);
        this.error.set(this.proformaContext ? this.t('sales.quotes.errorLoad') : this.t('sales.orders.errorLoad'));
      }
    });
  }

  onSelectionChange(selection: SalesOrderListItem[] | SalesOrderListItem | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  channelLabel(channelId: string): string {
    return this.channels().find(item => item.id === channelId)?.name ?? '-';
  }

  customerLabel(customerId: string): string {
    return this.customers().get(customerId) ?? customerId.slice(0, 8);
  }

  pageTitle(): string {
    return this.proformaContext ? this.t('sales.quotes.title') : this.t('sales.orders.title');
  }

  pageDescription(): string {
    return this.proformaContext ? this.t('sales.quotes.description') : this.t('sales.orders.description');
  }

  private listBasePath(): string {
    return this.proformaContext ? '/sales/quotes' : '/sales/orders';
  }

  openCreate(): void {
    void this.router.navigate([`${this.listBasePath()}/new`], { queryParams: this.currentQueryParams() });
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate([this.listBasePath(), id], { queryParams: this.currentQueryParams() });
  }

  private restoreFiltersFromQuery(): void {
    const params = this.route.snapshot.queryParamMap;
    this.filters.search = params.get('search') ?? '';
    this.filters.status = params.get('status');
    this.filters.channelId = params.get('channelId');
    this.showFilters.set((params.get('showFilters') ?? '1') !== '0');

    const page = Number(params.get('page') ?? '1');
    this.pageNumber = Number.isFinite(page) && page > 0 ? page : 1;
    this.tableFirst.set((this.pageNumber - 1) * this.pageSize);
    this.lastLazyFirst = this.tableFirst() - 1;
  }

  private syncQueryParams(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.currentQueryParams(),
      replaceUrl: true
    });
  }

  private currentQueryParams(): Record<string, string> {
    const params: Record<string, string> = {
      showFilters: this.showFilters() ? '1' : '0',
      page: String(this.pageNumber)
    };
    if (this.filters.search.trim()) {
      params['search'] = this.filters.search.trim();
    }
    if (this.filters.status) {
      params['status'] = this.filters.status;
    }
    if (this.filters.channelId) {
      params['channelId'] = this.filters.channelId;
    }
    return params;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
